import { Upload } from "@mui/icons-material";
import { Box, Fab, Grid, styled, Typography } from "@mui/material";
import {
  type VehicleExtraImagePosition,
  VehicleExtraImagePositions,
  type VehicleImagePosition,
  VehicleImagePositions,
} from "@repo/types";
import { useTranslation } from "react-i18next";
import carSchema from "@/assets/images/car-schema.jpg";
import type { DamagedCarImages } from "@/types/models/vehicle.types";
import CarImageUpload from "../car-image-upload";
import { ExtraImageButton } from "./ExtraImageButton";
import { PhotoDivider } from "./PhotoDivider";
import { SchemaImageButton } from "./SchemaImageButton";
import type { ImageConfig, PhotoSectionProps } from "./types";
import { getImageUrl } from "./utils";

const CarSchemaContainer = styled(Grid)<{ isPreview?: boolean }>(({ isPreview }) => ({
  backgroundImage: `url(${carSchema})`,
  backgroundPosition: "center",
  backgroundSize: "contain",
  backgroundRepeat: "no-repeat",
  minHeight: isPreview ? "200px" : "400px",
  minWidth: isPreview ? "150px" : "300px",
  margin: "auto",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: "center",
  textTransform: "capitalize",
}));

const ImageActionContainer = styled(Box)({
  position: "relative",
  display: "inline-block",
});

const UploadButton = styled(Fab)({
  position: "absolute",
  top: -12,
  right: -12,
  minWidth: "30px",
  maxWidth: "30px",
  minHeight: "30px",
  maxHeight: "30px",
  zIndex: 1,
  opacity: 0.6,
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    opacity: 1,
    transform: "scale(1.1)",
  },
});

export function PhotoSection({
  type,
  vehicle,
  images,
  isAdmin = false,
  isPreview = false,
  onImageClick,
}: PhotoSectionProps) {
  const { t } = useTranslation();
  const baseImageKey = `Cars/images${type}/${vehicle.id}-IMAGE_POSITION.FILE_EXTENSION`;

  const imageConfig: Record<VehicleImagePosition, Omit<ImageConfig, "key">> = {
    topImage: { tooltip: "delivery.photos.top", gridConfig: { xs: 12 } },
    topLeftImage: { tooltip: "delivery.photos.top-left", gridConfig: { xs: 6 } },
    topRightImage: { tooltip: "delivery.photos.top-right", gridConfig: { xs: 6 } },
    bottomLeftImage: { tooltip: "delivery.photos.bottom-left", gridConfig: { xs: 6 } },
    bottomRightImage: { tooltip: "delivery.photos.bottom-right", gridConfig: { xs: 6 } },
    bottomImage: { tooltip: "delivery.photos.bottom", gridConfig: { xs: 12 } },
  };
  const extraImageTooltips: Record<VehicleExtraImagePosition, string> = {
    dashboard: "delivery.photos.dashboard",
    cables: "delivery.photos.cables",
    cles: "delivery.photos.key",
  };

  return (
    <Box>
      {!isPreview && (
        <>
          <SectionTitle variant="h6">
            {type === "before" ? t("delivery.image-of-departure") : t("delivery.image-of-arrived")}
          </SectionTitle>
          <PhotoDivider />
        </>
      )}

      <CarSchemaContainer container spacing={2} isPreview={isPreview}>
        {VehicleImagePositions.map((key) => {
          const { tooltip: rawTooltip, gridConfig } = imageConfig[key];
          const imageUrl = getImageUrl(images, key);
          if (!rawTooltip) {
            return null;
          }
          const tooltip: string = rawTooltip;
          return (
            <Grid size={gridConfig} key={key}>
              <Box textAlign="center" sx={{ flexGrow: 1 }}>
                <CarImageUpload
                  viewOnly={!isAdmin}
                  tooltip={tooltip}
                  imagePos={key as keyof DamagedCarImages}
                  url={imageUrl}
                  baseImageKey={baseImageKey}
                  vehicle={vehicle}
                  _key={`images${type === "before" ? "Before" : "After"}`}
                  render={({
                    getRootProps,
                    getInputProps,
                    loading,
                    isSuccess,
                    handleImageClick,
                  }) => {
                    // tooltip is guaranteed to be a non-empty string after the early return check
                    const tooltipStr = tooltip;
                    const tooltipLabel = t(tooltipStr);
                    const uploadTitle = `${t("common.buttons.upload")} ${tooltipStr}`;
                    return (
                      <ImageActionContainer>
                        <SchemaImageButton
                          loading={loading}
                          isSuccess={isSuccess}
                          handleImageClick={
                            isPreview ? () => onImageClick?.(key) : handleImageClick
                          }
                          hasImage={!!imageUrl}
                          tooltip={tooltipLabel}
                          size={isPreview ? "small" : "large"}
                        />
                        {isAdmin && (
                          <Box {...getRootProps()}>
                            <UploadButton
                              color="secondary"
                              title={uploadTitle}
                              size={isPreview ? "small" : "large"}
                            >
                              <Upload sx={{ m: 0, fontSize: 16 }} />
                            </UploadButton>
                            <input {...getInputProps()} />
                          </Box>
                        )}
                      </ImageActionContainer>
                    );
                  }}
                />
              </Box>
            </Grid>
          );
        })}
      </CarSchemaContainer>

      <PhotoDivider />

      {!isPreview && (
        <Typography
          variant="h6"
          component="h3"
          sx={{ textAlign: "left", textTransform: "capitalize" }}
        >
          {t("delivery.form.additional-photos")}
        </Typography>
      )}

      <Grid container spacing={isPreview ? 0 : 1}>
        {VehicleExtraImagePositions.map((key, index) => {
          const tooltip = extraImageTooltips[key];
          const extraImageUrl = getImageUrl(images, key);
          const baseExtraImageKey = `Cars/extraImages/${vehicle.id}-${key}-IMAGE_POSITION.FILE_EXTENSION`;

          return (
            <Grid
              size={{ xs: 12 }}
              key={key}
              sx={{
                transform: isPreview ? "scale(0.8)" : "scale(1)",
              }}
            >
              <CarImageUpload
                viewOnly={!isAdmin}
                tooltip={t(tooltip)}
                imagePos="extraImages"
                extraImagePos={key}
                url={extraImageUrl}
                baseImageKey={baseExtraImageKey}
                vehicle={vehicle}
                _key={`images${type === "before" ? "Before" : "After"}`}
                render={({ getRootProps, getInputProps, loading, isSuccess, handleImageClick }) => (
                  <ImageActionContainer sx={{ width: "100%" }}>
                    <ExtraImageButton
                      loading={loading}
                      isSuccess={isSuccess}
                      handleImageClick={isPreview ? () => onImageClick?.(key) : handleImageClick}
                      image={t(tooltip)}
                      index={index}
                      extraImageUrl={extraImageUrl}
                    />
                    {isAdmin && !isPreview && (
                      <Box {...getRootProps()}>
                        <UploadButton
                          color="secondary"
                          title={`${t("common.buttons.upload")} ${t(tooltip)}`}
                          size="small"
                        >
                          <Upload sx={{ m: 0, fontSize: 16 }} />
                        </UploadButton>
                        <input {...getInputProps()} />
                      </Box>
                    )}
                  </ImageActionContainer>
                )}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
